import React, { useEffect, useState } from "react";
import {
    Loader2, Plus, Trash2, Edit2, Check, X, ChevronDown, FileText,
} from "lucide-react";
import {
    Popover, PopoverTrigger, PopoverContent,
} from "@/components/ui/popover";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import api, { formatApiError } from "@/lib/api";
import { toast } from "@/components/ui/sonner";

/**
 * iter-167 — Inline template picker that drops into the Message compose
 * dialog. Fetches /employer/message-templates and shows two surfaces:
 *   - Picker list: tap a template to insert its body
 *   - Manage pane: create / edit / delete templates without leaving the dialog
 */
export const TemplatePicker = ({ onInsert }) => {
    const [open, setOpen] = useState(false);
    const [mode, setMode] = useState("list"); // list | create | edit
    const [editing, setEditing] = useState(null);
    const [rows, setRows] = useState([]);
    const [meta, setMeta] = useState({ cap: 20, remaining: 20 });
    const [loading, setLoading] = useState(false);
    const [title, setTitle] = useState("");
    const [body, setBody] = useState("");
    const [saving, setSaving] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/employer/message-templates");
            setRows(data.templates || []);
            setMeta({ cap: data.cap, remaining: data.remaining });
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setLoading(false); }
    };

    useEffect(() => { if (open) load(); }, [open]);

    const resetForm = () => { setMode("list"); setEditing(null); setTitle(""); setBody(""); };

    const startCreate = () => { setMode("create"); setEditing(null); setTitle(""); setBody(""); };
    const startEdit = (r) => { setMode("edit"); setEditing(r); setTitle(r.title); setBody(r.body); };
    const insert = (r) => { onInsert?.(r.body); setOpen(false); };

    const save = async () => {
        const t = title.trim(); const b = body.trim();
        if (!t || !b) { toast.error("Title and body are required."); return; }
        setSaving(true);
        try {
            if (mode === "create") {
                await api.post("/employer/message-templates", { title: t, body: b });
                toast.success("Template saved.");
            } else {
                await api.patch(`/employer/message-templates/${editing.id}`, { title: t, body: b });
                toast.success("Template updated.");
            }
            resetForm();
            load();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setSaving(false); }
    };

    const remove = async (r) => {
        if (!window.confirm(`Delete template "${r.title}"?`)) return;
        try {
            await api.delete(`/employer/message-templates/${r.id}`);
            toast.success("Template deleted.");
            load();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <button
                    type="button"
                    data-testid="tpl-picker-trigger"
                    className="inline-flex items-center gap-1 rounded-full border border-border bg-card px-2.5 py-1 text-[11px] font-semibold text-foreground hover:bg-muted/40"
                    title="Pick or manage saved templates"
                >
                    <FileText size={11} /> Templates
                    <ChevronDown size={10} />
                </button>
            </PopoverTrigger>
            <PopoverContent align="start" className="w-96 p-3" data-testid="tpl-picker-pane">
                {mode === "list" && (
                    <div>
                        <div className="flex items-center justify-between mb-2">
                            <div className="text-xs font-semibold text-foreground">Saved templates</div>
                            <button
                                onClick={startCreate}
                                disabled={meta.remaining === 0}
                                data-testid="tpl-new"
                                className="inline-flex items-center gap-1 rounded-full border border-emerald-500/40 bg-emerald-500/10 px-2 py-0.5 text-[10px] font-semibold text-emerald-700 dark:text-emerald-400 hover:bg-emerald-500/15 disabled:opacity-60"
                                title={meta.remaining === 0 ? `Cap reached (${meta.cap}). Delete one to add.` : "Create a template"}
                            >
                                <Plus size={10} /> New
                            </button>
                        </div>
                        <div className="text-[10px] text-muted-foreground mb-2">
                            {meta.remaining} of {meta.cap} slots remaining
                        </div>
                        {loading ? (
                            <div className="py-6 flex justify-center"><Loader2 size={14} className="animate-spin text-emerald-500" /></div>
                        ) : rows.length === 0 ? (
                            <div className="text-xs text-muted-foreground py-4 text-center" data-testid="tpl-empty">
                                No templates yet. Save your best openers here.
                            </div>
                        ) : (
                            <ul className="space-y-1.5 max-h-72 overflow-y-auto pr-1">
                                {rows.map((r) => (
                                    <li
                                        key={r.id}
                                        data-testid={`tpl-row-${r.id}`}
                                        className="group rounded-lg border border-border bg-card p-2 hover:border-emerald-500/40"
                                    >
                                        <div className="flex items-start gap-2">
                                            <button
                                                onClick={() => insert(r)}
                                                className="flex-1 min-w-0 text-left"
                                                data-testid={`tpl-insert-${r.id}`}
                                            >
                                                <div className="text-xs font-semibold text-foreground truncate">{r.title}</div>
                                                <div className="text-[11px] text-muted-foreground line-clamp-2 mt-0.5">{r.body}</div>
                                            </button>
                                            <div className="flex items-center gap-0.5 opacity-70 group-hover:opacity-100">
                                                <button
                                                    onClick={() => startEdit(r)}
                                                    data-testid={`tpl-edit-${r.id}`}
                                                    className="p-1 rounded hover:bg-muted/60"
                                                    title="Edit"
                                                ><Edit2 size={11} /></button>
                                                <button
                                                    onClick={() => remove(r)}
                                                    data-testid={`tpl-delete-${r.id}`}
                                                    className="p-1 rounded hover:bg-rose-500/15 text-rose-600 dark:text-rose-400"
                                                    title="Delete"
                                                ><Trash2 size={11} /></button>
                                            </div>
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                )}
                {mode !== "list" && (
                    <div>
                        <div className="flex items-center justify-between mb-2">
                            <div className="text-xs font-semibold text-foreground">
                                {mode === "create" ? "New template" : "Edit template"}
                            </div>
                            <button onClick={resetForm} data-testid="tpl-form-cancel" className="p-1 rounded hover:bg-muted/60" title="Back">
                                <X size={11} />
                            </button>
                        </div>
                        <label className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">Title</label>
                        <Input
                            value={title} onChange={(e) => setTitle(e.target.value)}
                            maxLength={80} placeholder="Senior CS Lead intro"
                            data-testid="tpl-form-title" className="h-8 text-sm mt-0.5"
                        />
                        <label className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground mt-2 block">Body</label>
                        <Textarea
                            value={body} onChange={(e) => setBody(e.target.value)}
                            rows={5} maxLength={2000}
                            placeholder="Hi — your Simonara passport caught my eye. We're hiring a Sr. CS Lead…"
                            data-testid="tpl-form-body" className="text-sm mt-0.5"
                        />
                        <div className="flex items-center justify-between mt-2">
                            <span className="text-[10px] text-muted-foreground">{body.length}/2000</span>
                            <button
                                onClick={save}
                                disabled={saving || !title.trim() || !body.trim()}
                                data-testid="tpl-form-save"
                                className="btn-green text-xs px-3 py-1.5 inline-flex items-center gap-1 disabled:opacity-60"
                            >
                                {saving ? <Loader2 size={11} className="animate-spin" /> : <Check size={11} />}
                                {mode === "create" ? "Save template" : "Save changes"}
                            </button>
                        </div>
                    </div>
                )}
            </PopoverContent>
        </Popover>
    );
};

export default TemplatePicker;
